import React from 'react';
import { Link } from 'react-router-dom';
import Button from './Button';
import { useCurrency } from '../../context/CurrencyContext';

/**
 * PricingCard Component
 * Displays a pricing plan with features and CTA
 */
interface PricingCardProps {
  name: string;
  description: string;
  price: number | null;
  billing: string;
  features: string[];
  mostPopular?: boolean;
  ctaText: string;
  ctaLink: string;
  className?: string;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  description,
  price,
  billing,
  features,
  mostPopular = false,
  ctaText,
  ctaLink,
  className = '',
}) => {
  const { formatCurrency } = useCurrency();
  return (
    <div
      className={`
        relative rounded-xl overflow-hidden border transition-all
        ${mostPopular 
          ? 'border-primary shadow-lg shadow-primary/10 scale-105 z-10 bg-white' 
          : 'border-neutral-200 bg-white/50 hover:shadow-md'}
        ${className}
      `}
    >
      {mostPopular && (
        <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
          Most Popular
        </div>
      )}

      <div className="p-6 md:p-8">
        <h3 className="text-2xl font-bold mb-2">{name}</h3>
        <p className="text-neutral-600 mb-6">{description}</p>

        <div className="mb-6">
          {price !== null ? (
            <div className="flex items-baseline">
              <span className="text-4xl font-bold">{formatCurrency(price)}</span>
              <span className="text-neutral-500 ml-2">{billing}</span>
            </div>
          ) : (
            <div className="flex items-baseline">
              <span className="text-3xl font-bold">{billing}</span>
            </div>
          )}
        </div>

        <hr className="border-neutral-200 mb-6" />

        <ul className="space-y-3 mb-8">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg 
                className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
                  feature.includes('Everything in') 
                    ? 'text-primary' 
                    : 'text-neutral-700'
                }`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className={feature.includes('Everything in') ? 'font-medium' : ''}>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          variant={mostPopular ? 'primary' : 'outline'}
          onClick={() => window.location.href = ctaLink}
          className="w-full"
          size="lg"
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};

export default PricingCard;